// Nouvelle révision du devis après le lancement (carnet de chantier, incrément 7 ; conception § 2.8).
// L'écran d'écart montre chaque ligne du devis qui a changé (ajoutée, retirée, quantité, heures, libellé) et ce qui arrivera aux
// étapes du chantier si on la reporte ; le chargé d'affaires coche ce qu'il reporte, place les étapes nouvelles, rapproche au
// besoin « c'est la même ligne », puis « Reporter ». Une étape déjà faite n'est jamais retirée : elle reste, marquée, à arbitrer.
// Le report est idempotent (le rejouer ne change rien) ; aucun montant n'est affiché.
// Téléphone d'abord : le bouton « Reporter » est dans la barre collante (.barre-collante), visible sans défiler, au-dessus des onglets.
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, CheckCircle2, GitCompareArrows, Link2, Unlink } from 'lucide-react';
import { api, messageErreur } from '../../../lib/api.ts';
import { date, dateHeure, nombre, pluriel } from '../../../lib/format.ts';
import {
  Alerte, Badge, Bouton, Chargement, Dialogue, EntetePage, EtatErreur, EtatVide, FilContexte, LienBouton, Panneau, useNotifier,
} from '../../../components/ui.tsx';
import { heures, phraseEcart } from './commun.tsx';
import type { CompteRenduReport, ConsequenceEtape, EcartRevision, ItemEcart, ValeursLigne } from './types.ts';

const SORTES: Record<ItemEcart['sorte'], { libelle: string; ton: 'info' | 'ambre' | 'neutre' | 'accent' }> = {
  ajoutee: { libelle: 'Ajoutée', ton: 'info' }, retiree: { libelle: 'Retirée', ton: 'ambre' }, modifiee: { libelle: 'Modifiée', ton: 'accent' },
  inchangee: { libelle: 'Inchangée', ton: 'neutre' },
};
const q = (v: string | null | undefined, u: string) => (v === null || v === undefined ? '—' : `${nombre(v, 4)} ${u}`);
const libelleDe = (i: ItemEcart) => (i.apres ?? i.avant)!.label;

/** Avant → après, seulement ce qui change (heures par unité et section : vue Expert). */
function AvantApres({ i }: { i: ItemEcart }) {
  const a = i.avant; const n = i.apres;
  const lignes: { quoi: string; avant: string; apres: string; expert?: boolean }[] = [];
  if (a && n) {
    if (a.label !== n.label) lignes.push({ quoi: 'Libellé', avant: a.label, apres: n.label });
    if (a.quantite !== n.quantite || a.unite !== n.unite) lignes.push({ quoi: 'Quantité', avant: q(a.quantite, a.unite), apres: q(n.quantite, n.unite) });
    if (a.heures !== n.heures) lignes.push({ quoi: 'Heures', avant: heures(a.heures) ?? 'à renseigner', apres: heures(n.heures) ?? 'à renseigner' });
    if (a.heures_par_unite !== n.heures_par_unite) {
      lignes.push({ quoi: 'Heures par unité', avant: heures(a.heures_par_unite) ?? 'à renseigner', apres: heures(n.heures_par_unite) ?? 'à renseigner', expert: true });
    }
  }
  const seule: ValeursLigne | null = !a ? n : !n ? a : null;
  return (
    <div className="rv-valeurs">
      {seule && <p className="petit chiffre" style={{ margin: 0 }}>{q(seule.quantite, seule.unite)}{seule.heures !== null ? ` — ${heures(seule.heures)}` : ' — temps à renseigner'}
        <span className="discret expert-seulement"> — section {seule.section}{seule.reference ? `, réf. ${seule.reference}` : ''}</span></p>}
      {lignes.length > 0 && (
        <dl className="rv-changements">
          {lignes.map((l) => (
            <div key={l.quoi} className={l.expert ? 'expert-seulement' : undefined}>
              <dt>{l.quoi}</dt>
              <dd className="chiffre"><span className="rv-avant">{l.avant}</span><ArrowRight aria-label="devient" /><strong>{l.apres}</strong></dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

function phraseConsequence(c: ConsequenceEtape, revNo: number, titre: string) {
  const fait = Number(c.qty_done) > 0 ? ` (fait : ${q(c.qty_done, c.unit)})` : '';
  // L'étape porte le même libellé que la ligne : « L'étape » suffit (moins de texte sur téléphone).
  const nom = c.label === titre ? 'L’étape' : `« ${c.label} »`;
  switch (c.quoi) {
    case 'retiree': return `${nom} n’a jamais été cochée : elle sera retirée du chantier (gardée, barrée, avec le motif).`;
    case 'reste_marquee': return `${nom}${fait || ' (faite)'} RESTE sur le chantier, marquée « faite — retirée du devis par la révision ${revNo} », à arbitrer (travaux supplémentaires, avoir…).`;
    case 'complement': return `${nom} est faite : elle garde ${q(c.qty_planned, c.unit)} ; une étape « complément » de ${q(c.qty ?? null, c.unit)} est ajoutée, à faire.`;
    case 'modifiee': {
      const nv = c.nouvelles ?? {};
      const morceaux = [
        nv.qty_planned !== undefined ? `quantité ${q(c.qty_planned, c.unit)} → ${q(nv.qty_planned, nv.unit ?? c.unit)}` : null,
        nv.hours_per_unit !== undefined ? `heures par unité → ${heures(nv.hours_per_unit) ?? 'à renseigner'}` : null,
        nv.label !== undefined ? `libellé → « ${nv.label} »` : null,
        nv.unit !== undefined && nv.qty_planned === undefined ? `unité → ${nv.unit}` : null,
        nv.quote_line_key ? 'suit la nouvelle ligne du devis' : null,
      ].filter(Boolean);
      return `${nom}${fait} : ${morceaux.join(', ')}. Ce qui est fait reste fait.`;
    }
    default: return `${nom} : ${c.pourquoi ?? 'rien à changer'}.`;
  }
}

function CarteItem({ i, revNo, coche, basculer, postes, placement, placer, ajoutees, rapprocher, defaire, rapprochementId, lecture }: {
  i: ItemEcart; revNo: number; coche: boolean; basculer: () => void; postes: { id: string; label: string }[];
  placement: string | undefined; placer: (posteId: string | null) => void; ajoutees: ItemEcart[];
  rapprocher: (ancienne: string, nouvelle: string) => void; defaire: (id: string) => void; rapprochementId: string | null; lecture: boolean;
}) {
  const [avec, setAvec] = useState('');
  const s = SORTES[i.sorte];
  const libres = ajoutees.filter((x) => !x.rapprochee);
  const tries = [...libres].sort((x, y) => Number(i.candidats.includes(y.cle)) - Number(i.candidats.includes(x.cle)));
  return (
    <li className={`rv-item rv-${i.sorte}${coche ? ' est-coche' : ''}`}>
      <label className="rv-case">
        <input type="checkbox" checked={coche} onChange={basculer} disabled={lecture || !i.reportable} aria-describedby={`rv-${i.cle}`} />
        <span className="rv-titre">{libelleDe(i)}</span>
        <Badge ton={s.ton} icone={false}>{s.libelle}</Badge>
        {i.rapprochee && <Badge ton="info" icone={<Link2 size={14} aria-hidden />}>Même ligne (à la main)</Badge>}
      </label>
      <div className="rv-corps" id={`rv-${i.cle}`}>
        <AvantApres i={i} />
        {i.etapes.length > 0 && (
          <ul className="rv-consequences petit">
            {i.etapes.map((c) => <li key={c.etape_id} className={c.quoi === 'reste_marquee' ? 'rv-marquee' : undefined}>{phraseConsequence(c, revNo, libelleDe(i))}</li>)}
          </ul>
        )}
        {i.ajout && (
          <div className="rv-placement">
            <p className="petit" style={{ margin: 0 }}>Nouvelle étape{i.ajout.etape.label !== libelleDe(i) ? ` « ${i.ajout.etape.label} »` : ''} à faire.</p>
            {lecture ? null : (
              <label className="champ rv-poste">
                <span className="champ-libelle">Placée dans le poste</span>
                <select className="saisie" value={placement ?? i.ajout.poste.id ?? ''} onChange={(e) => placer(e.target.value || null)}>
                  {!i.ajout.poste.id && <option value="">Nouveau poste « {i.ajout.poste.label} »</option>}
                  {postes.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                </select>
              </label>
            )}
          </div>
        )}
        {i.a_poser && (
          <p className="petit" style={{ margin: 0 }}>À poser : {i.a_poser.label}{i.a_poser.reference ? ` (${i.a_poser.reference})` : ''} —{' '}
            {i.a_poser.quoi === 'ajout' ? `ajouté, ${q(i.a_poser.qty_apres, i.a_poser.unit)}` : i.a_poser.quoi === 'retrait' ? 'retiré de la liste'
              : `${q(i.a_poser.qty_avant, i.a_poser.unit)} → ${q(i.a_poser.qty_apres, i.a_poser.unit)}`}.</p>
        )}
        {i.note && <p className="petit secondaire" style={{ margin: 0 }}>{i.note}</p>}
        {!lecture && i.sorte === 'retiree' && libres.length > 0 && (
          <div className="rv-rapprocher">
            <label className="champ">
              <span className="champ-libelle">C’est la même ligne que…{i.candidats.length ? ' (proposée : même section, libellé proche)' : ''}</span>
              <select className="saisie" value={avec} onChange={(e) => setAvec(e.target.value)}>
                <option value="">— choisir une ligne ajoutée —</option>
                {tries.map((x) => <option key={x.cle} value={x.cle}>{i.candidats.includes(x.cle) ? '★ ' : ''}{libelleDe(x)}</option>)}
              </select>
            </label>
            <Bouton icone={<Link2 aria-hidden />} disabled={!avec} onClick={() => rapprocher(i.ancienne_cle!, avec)}>Rapprocher</Bouton>
          </div>
        )}
        {!lecture && i.rapprochee && rapprochementId && (
          <div><Bouton icone={<Unlink aria-hidden />} onClick={() => defaire(rapprochementId)}>Ce n’est pas la même ligne</Bouton></div>
        )}
      </div>
    </li>
  );
}

function CompteRendu({ r }: { r: CompteRenduReport }) {
  const b = r.bilan;
  const lignes = [
    b.etapes_ajoutees ? pluriel(b.etapes_ajoutees, 'étape ajoutée', 'étapes ajoutées') : null,
    b.etapes_retirees ? pluriel(b.etapes_retirees, 'étape retirée', 'étapes retirées') : null,
    b.etapes_marquees ? `${pluriel(b.etapes_marquees, 'étape faite marquée', 'étapes faites marquées')} « retirée du devis » (à arbitrer)` : null,
    b.etapes_modifiees ? pluriel(b.etapes_modifiees, 'étape modifiée', 'étapes modifiées') : null,
    b.complements ? pluriel(b.complements, 'étape complément', 'étapes complément') : null,
    b.postes_crees ? pluriel(b.postes_crees, 'poste créé', 'postes créés') : null,
    b.a_poser ? pluriel(b.a_poser, 'changement « À poser »', 'changements « À poser »') : null,
    b.non_reportes ? `${pluriel(b.non_reportes, 'changement non reporté', 'changements non reportés')} (choix du chargé d’affaires)` : null,
  ].filter(Boolean) as string[];
  return (
    <Alerte ton="succes" titre={`Mis à jour depuis la révision ${r.rev_no} le ${dateHeure(r.le)}${r.par ? ` par ${r.par}` : ''}`}>
      {lignes.length ? `${lignes.join(' · ')}.` : 'Aucun changement reporté : le chantier suit la nouvelle révision.'} Le détail de chaque étape est dans son historique (onglet Avancement).
    </Alerte>
  );
}

export function PageRevision() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const revisionLue = params.get('revision');
  const naviguer = useNavigate();
  const qc = useQueryClient();
  const notifier = useNotifier();
  const cle = ['chantier-revision', id, revisionLue];
  const r = useQuery({ queryKey: cle, queryFn: () => api.get<EcartRevision>(`/api/chantiers/${id}/ecart-revision${revisionLue ? `?revision=${revisionLue}` : ''}`) });
  const [coches, setCoches] = useState<Set<string> | null>(null);
  const [placements, setPlacements] = useState<Record<string, string>>({});
  const [confirmer, setConfirmer] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const v = r.data;
  const items = v?.ecart?.items ?? [];
  // Tout ce qui change est coché d'office ; le chargé d'affaires décoche ce qu'il ne reporte pas.
  useEffect(() => { if (v?.ecart && coches === null) setCoches(new Set(items.filter((i) => i.reportable).map((i) => i.cle))); }, [v?.ecart]);
  const rapId = useMemo(() => new Map((v?.rapprochements ?? []).map((x) => [x.nouvelle_cle, x.id])), [v?.rapprochements]);
  if (r.isLoading) return <Chargement />;
  if (r.isError) return <EtatErreur erreur={r.error} reessayer={() => r.refetch()} />;
  const c = v!.chantier;
  const fil = <FilContexte noeuds={[{ type: '', libelle: 'Chantiers', to: '/interventions/chantiers' }, { type: '', libelle: c.reference ?? c.title, to: `/interventions/chantiers/${id}` },
    { type: '', libelle: v!.revision ? `Révision ${v!.revision.rev_no}` : 'Révision du devis' }]} />;
  const retour = <LienBouton variante="principal" to={`/interventions/chantiers/${id}`}>Revenir au chantier</LienBouton>;

  if (!v!.revision || !v!.ecart) {
    const fait = v!.deja ?? v!.dernier_report;
    return (
      <div className="pile-l">
        <EntetePage fil={fil} titre="Révision du devis" sousTitre={<span className="petit">{c.title}</span>} />
        {fait ? <CompteRendu r={fait} /> : <EtatVide icone={<CheckCircle2 aria-hidden />} titre="Rien à reporter" texte={v!.motif ?? undefined} />}
        {fait && v!.motif && !v!.deja && <p className="petit secondaire" style={{ margin: 0 }}>{v!.motif}</p>}
        <div className="barre-actions barre-collante"><span className="petit barre-collante-resume">{fait ? `Révision ${fait.rev_no} reportée` : 'Aucune révision à reporter'}</span>{retour}</div>
      </div>
    );
  }

  const rev = v!.revision; const ec = v!.ecart;
  const reportables = items.filter((i) => i.reportable);
  const choisis = coches ?? new Set<string>();
  const nbChoisis = reportables.filter((i) => choisis.has(i.cle)).length;
  const inchangees = items.filter((i) => !i.reportable);
  const ajoutees = items.filter((i) => i.sorte === 'ajoutee');
  const lecture = !!v!.motif;
  const basculer = (k: string) => setCoches((s) => { const n = new Set(s ?? []); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const recharger = async () => { await qc.invalidateQueries({ queryKey: ['chantier-revision', id] }); };
  const rapprocher = async (ancienneCle: string, nouvelleCle: string) => {
    try {
      await api.post(`/api/chantiers/${id}/rapprochements`, { revisionId: rev.id, ancienneCle, nouvelleCle });
      notifier('Lignes rapprochées : l’étape suivra la nouvelle ligne.');
      setCoches(null); await recharger();
    } catch (e) { notifier(messageErreur(e), true); }
  };
  const defaire = async (rid: string) => {
    try { await api.del(`/api/chantiers/${id}/rapprochements/${rid}`); notifier('Rapprochement défait.'); setCoches(null); await recharger(); }
    catch (e) { notifier(messageErreur(e), true); }
  };
  const reporter = async () => {
    setEnvoi(true);
    try {
      const pl = Object.fromEntries(Object.entries(placements).filter(([k]) => choisis.has(k)));
      await api.post(`/api/chantiers/${id}/reporter-revision`, { revisionId: rev.id, choix: reportables.filter((i) => choisis.has(i.cle)).map((i) => i.cle),
        placements: pl, version: c.version });
      await Promise.all([qc.invalidateQueries({ queryKey: ['chantier', id] }), qc.invalidateQueries({ queryKey: ['chantier-avancement', id] }), recharger()]);
      notifier(`Révision ${rev.rev_no} reportée sur le chantier.`);
      naviguer(`/interventions/chantiers/${id}/revision?revision=${rev.id}`, { replace: true });
    } catch (e) {
      notifier(messageErreur(e), true);
      await recharger();
    } finally { setEnvoi(false); setConfirmer(false); }
  };
  const phrase = phraseEcart(ec.resume);
  const choisisItems = reportables.filter((i) => choisis.has(i.cle));
  const compte = (f: (i: ItemEcart) => boolean) => choisisItems.filter(f).length;
  const marquees = choisisItems.flatMap((i) => i.etapes).filter((x) => x.quoi === 'reste_marquee').length;

  return (
    <div className="pile-l rv-page">
      <EntetePage fil={fil} titre={`Reporter la révision ${rev.rev_no} du devis`}
        sousTitre={<span className="petit">Acceptée le {date(rev.acceptee_le)} · le chantier suit la révision {v!.depuis?.rev_no}<span className="expert-seulement"> · {c.title}</span></span>} />
      {lecture ? <Alerte ton="ambre">{v!.motif}</Alerte> : (
        <p className="secondaire rv-intro" style={{ margin: 0 }}>
          <span className="expert-seulement">{phrase ? `${phrase}. ` : ''}</span>Rien ne change tant que vous n’avez pas reporté ; une étape déjà faite n’est jamais retirée.
        </p>
      )}

      <Panneau serre titre={`${pluriel(reportables.length, 'changement', 'changements')} à reporter`}
        actions={!lecture && reportables.length > 1 ? (nbChoisis === reportables.length
          ? <Bouton onClick={() => setCoches(new Set())}>Tout décocher</Bouton>
          : <Bouton onClick={() => setCoches(new Set(reportables.map((i) => i.cle)))}>Tout cocher</Bouton>) : undefined}>
        {reportables.length === 0
          ? <p className="secondaire" style={{ margin: 0, padding: 'var(--e-4)' }}>Aucun changement de cette révision ne touche les étapes du chantier. « Reporter » fera simplement suivre la révision {rev.rev_no} au chantier.</p>
          : (
            <ol className="rv-liste">
              {reportables.map((i) => (
                <CarteItem key={i.cle} i={i} revNo={rev.rev_no} coche={choisis.has(i.cle)} basculer={() => basculer(i.cle)} postes={v!.postes ?? []}
                  placement={placements[i.cle]} placer={(p) => setPlacements((x) => { const n = { ...x }; if (p) n[i.cle] = p; else delete n[i.cle]; return n; })}
                  ajoutees={ajoutees} rapprocher={rapprocher} defaire={defaire} rapprochementId={i.nouvelle_cle ? rapId.get(i.nouvelle_cle) ?? null : null} lecture={lecture} />
              ))}
            </ol>
          )}
      </Panneau>

      {(inchangees.length > 0 || ec.sans_effet.length > 0) && (
        <details className="panneau rv-autres">
          <summary className="panneau-entete"><strong>{pluriel(inchangees.length + ec.sans_effet.length, 'autre ligne', 'autres lignes')}</strong>
            <span className="petit secondaire"> — inchangées ou sans effet sur le chantier</span></summary>
          <ul className="panneau-corps petit rv-autres-liste">
            {inchangees.map((i) => <li key={i.cle}>{libelleDe(i)} — {i.sorte === 'inchangee' ? 'inchangée' : i.note ?? 'rien à reporter'}</li>)}
            {ec.sans_effet.map((l) => <li key={l.cle}>{(l.apres ?? l.avant)!.label} — {SORTES[l.sorte].libelle.toLowerCase()}, sans effet sur le chantier ({l.motif})
              {l.avant && l.apres && l.avant.quantite !== l.apres.quantite ? ` : ${q(l.avant.quantite, l.avant.unite)} → ${q(l.apres.quantite, l.apres.unite)}` : ''}</li>)}
          </ul>
        </details>
      )}

      <div className="barre-actions barre-collante">
        <span className="petit chiffre barre-collante-resume">{nbChoisis} coché{nbChoisis > 1 ? 's' : ''} sur {reportables.length}</span>
        {lecture ? retour : (
          <Bouton variante="principal" icone={<GitCompareArrows aria-hidden />} onClick={() => setConfirmer(true)}>Reporter</Bouton>
        )}
      </div>
      <p className="petit discret" style={{ margin: 0 }}><Link to={`/interventions/chantiers/${id}`}>Revenir au chantier sans reporter</Link></p>

      <Dialogue titre={`Reporter la révision ${rev.rev_no} ?`} ouvert={confirmer} fermer={() => setConfirmer(false)}
        pied={<><Bouton onClick={() => setConfirmer(false)}>Annuler</Bouton>
          <Bouton variante="principal" chargement={envoi} onClick={reporter}>Reporter {nbChoisis ? pluriel(nbChoisis, 'changement') : 'sans changement'}</Bouton></>}>
        <ul className="pile" style={{ gap: 'var(--e-2)', margin: 0, paddingLeft: '1.2em' }}>
          {nbChoisis === 0 && <li>Aucun changement coché : le chantier suivra la révision {rev.rev_no} sans modification de ses étapes.</li>}
          {compte((i) => !!i.ajout) > 0 && <li>{pluriel(compte((i) => !!i.ajout), 'étape ajoutée', 'étapes ajoutées')}</li>}
          {compte((i) => i.etapes.some((x) => x.quoi === 'retiree')) > 0 && <li>{pluriel(choisisItems.flatMap((i) => i.etapes).filter((x) => x.quoi === 'retiree').length, 'étape retirée', 'étapes retirées')} (jamais cochées)</li>}
          {marquees > 0 && <li>{pluriel(marquees, 'étape faite reste', 'étapes faites restent')}, marquée{marquees > 1 ? 's' : ''} « retirée du devis », à arbitrer</li>}
          {compte((i) => i.etapes.some((x) => x.quoi === 'modifiee' || x.quoi === 'complement')) > 0 && <li>{pluriel(compte((i) => i.etapes.some((x) => x.quoi === 'modifiee' || x.quoi === 'complement')), 'ligne modifiée', 'lignes modifiées')} : ce qui est fait reste fait</li>}
          {reportables.length - nbChoisis > 0 && <li>{pluriel(reportables.length - nbChoisis, 'changement non reporté', 'changements non reportés')} : il ne sera plus proposé.</li>}
        </ul>
        <p className="petit secondaire" style={{ margin: 'var(--e-3) 0 0' }}>Le chantier notera « mis à jour depuis la révision {rev.rev_no} ». Chaque valeur prévue changée reste lisible dans l’historique de l’étape.</p>
      </Dialogue>
    </div>
  );
}
